import { useEffect, useRef } from 'react';
import { trackIncidentExpansion, trackIncidentCollapse } from '../../../../utils/modernUITracking';

/**
 * Effect-driven expand/collapse tracking for incident UI (card on mobile, row
 * on desktop). Fires Expand on false → true, Collapse on true → false (with
 * viewDurationMs). A row that mounts already-expanded does NOT fire a spurious
 * Expand. Unmount-while-expanded fires Collapse so virtualized lists / route
 * changes don't silently drop long-engagement sessions from the histogram.
 *
 * `section` and `newrelic` are mirrored into refs so the unmount cleanup reads
 * the latest values without retriggering the transition effect on prop-identity
 * churn (e.g., a parent re-render rebuilding the newrelic reference).
 *
 * **Assumption: `number` is stable for the component's lifetime.** IncidentSection
 * keys each row by `problem.number`, so React tears down + remounts when the
 * underlying incident changes — `number` is never observed to change mid-life.
 * `source` is a static string literal at each call site ('card' or 'row'), also
 * stable. Neither is mirrored into a ref for that reason. A future caller that
 * reuses this hook in a virtualized list that recycles rows across incidents
 * must either key by `number` at the parent, or refactor this hook to mirror
 * `number` the way `section` is mirrored — otherwise unmount-Collapse will
 * report a stale incident ID.
 *
 * Replaces the previously-duplicated tracking blocks in IncidentCard and
 * IncidentListRow, and closes the prior asymmetry where IncidentCard could
 * fire Expand on mount-while-expanded.
 */
export function useIncidentExpansionTracking({ number, isExpanded, source, section, newrelic }) {
  const expandStartTime = useRef(null);

  const sectionRef = useRef(section);
  useEffect(() => { sectionRef.current = section; });
  const newrelicRef = useRef(newrelic);
  useEffect(() => { newrelicRef.current = newrelic; });

  // prev === null on initial mount → not a user transition, return early.
  const prevIsExpandedRef = useRef(null);
  useEffect(() => {
    const prev = prevIsExpandedRef.current;
    prevIsExpandedRef.current = isExpanded;
    if (prev === null) return;
    if (prev === isExpanded) return;

    if (isExpanded) {
      expandStartTime.current = Date.now();
      trackIncidentExpansion(number, {
        source,
        section: sectionRef.current,
      }, newrelicRef.current);
    } else if (expandStartTime.current !== null) {
      const viewDurationMs = Date.now() - expandStartTime.current;
      expandStartTime.current = null;
      trackIncidentCollapse(number, {
        source,
        section: sectionRef.current,
        viewDurationMs,
      }, newrelicRef.current);
    }
  }, [isExpanded, number, source]);

  useEffect(() => {
    return () => {
      if (expandStartTime.current !== null) {
        trackIncidentCollapse(number, {
          source,
          section: sectionRef.current,
          viewDurationMs: Date.now() - expandStartTime.current,
        }, newrelicRef.current);
        expandStartTime.current = null;
      }
    };
  }, [number, source]);
}
